import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import tokenStore from '../../utils/tokenStore';
import { formatVnd, memberLevelLabel, FREE_TIER_FEATURES, previewOrderAmount, mergePackagesFromApi, normalizeTierLabel, packageTierLabel } from '../../constants/membershipPackages';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import '../../css/membership.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function submitSepayCheckout(checkout) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = checkout.checkoutUrl;
  form.style.display = 'none';
  Object.entries(checkout.fields || {}).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = String(value ?? '');
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

const MembershipPage = () => {
  const { data, authReady } = useContext(DataContext);
  const { refreshAccess } = useFeatureAccess();
  const isRelative = data?.role === 'relative';
  const isElderly = data?.role === 'elderly';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [membership, setMembership] = useState(null);
  const [selectedElderlyId, setSelectedElderlyId] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);

  const packages = useMemo(
    () => mergePackagesFromApi(membership?.packages),
    [membership?.packages]
  );
  const linkedElderlies = membership?.linkedElderlies || [];

  const loadMembership = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/membership`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Không thể tải thông tin membership');
      }
      setMembership(json.data);
      if (json.data?.linkedElderlies?.length === 1) {
        setSelectedElderlyId(String(json.data.linkedElderlies[0].id));
      }
    } catch (err) {
      setError(err.message || 'Không thể tải thông tin membership');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollOrderStatus = useCallback(async (invoice) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/membership/orders/${encodeURIComponent(invoice)}`);
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success && json.data?.status === 'paid') {
        setSuccess('Thanh toán thành công! Gói hội viên đã được kích hoạt.');
        setPendingOrder(null);
        sessionStorage.removeItem('tamcare_pending_order');
        await loadMembership();
        await refreshAccess();
        window.dispatchEvent(new Event('tamcare:membership-updated'));
        return true;
      }
    } catch (_) {}
    return false;
  }, [loadMembership, refreshAccess]);

  useEffect(() => {
    if (!authReady) return;

    const orderInvoice = searchParams.get('order');
    const status = searchParams.get('status');
    const awaitingPaymentReturn = orderInvoice && status === 'success';

    if (!data?.id) {
      if (awaitingPaymentReturn) {
        const timer = setTimeout(() => {
          if (!tokenStore.getToken()) {
            navigate('/login', {
              replace: true,
              state: { from: `/membership?order=${encodeURIComponent(orderInvoice)}&status=success` },
            });
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
      navigate('/login', { replace: true, state: { from: '/membership' } });
      return;
    }
    loadMembership();
  }, [authReady, data?.id, loadMembership, navigate, searchParams]);

  useEffect(() => {
    const orderInvoice = searchParams.get('order');
    const status = searchParams.get('status');
    if (!orderInvoice) {
      const stored = sessionStorage.getItem('tamcare_pending_order');
      if (stored) {
        setPendingOrder(stored);
        setSuccess('Đang xác nhận thanh toán từ SePay...');
      }
      return;
    }

    if (status === 'success') {
      setPendingOrder(orderInvoice);
      setSuccess('Đang xác nhận thanh toán từ SePay...');
    } else if (status === 'cancel') {
      setError('Bạn đã hủy thanh toán.');
    } else if (status === 'error') {
      setError('Thanh toán không thành công. Vui lòng thử lại.');
    }

    searchParams.delete('order');
    searchParams.delete('status');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!pendingOrder || !authReady || !data?.id) return undefined;

    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      const done = await pollOrderStatus(pendingOrder);
      if (done || attempts >= 30) {
        clearInterval(pollRef.current);
        if (!done && attempts >= 30) {
          setError('Chưa nhận được xác nhận thanh toán. Nếu đã chuyển khoản, vui lòng đợi vài phút hoặc liên hệ hỗ trợ.');
        }
      }
    };

    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => clearInterval(pollRef.current);
  }, [pendingOrder, pollOrderStatus, authReady, data?.id]);

  const selectedElderly = linkedElderlies.find((e) => String(e.id) === String(selectedElderlyId));
  const modalAmount = selectedPackage && selectedElderly
    ? previewOrderAmount(selectedElderly, selectedPackage.id)
    : selectedPackage?.cost;

  const handlePurchase = async () => {
    if (!selectedPackage || !selectedElderlyId) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/membership/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          elderlyId: Number(selectedElderlyId),
          packageId: selectedPackage.id,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        const code = json?.code;
        let message = json?.message || 'Không thể tạo đơn thanh toán';
        if (code === 'db_not_migrated') {
          message = 'Hệ thống chưa sẵn sàng thanh toán. Admin cần chạy migration membership trên server.';
        } else if (code === 'sepay_not_configured') {
          message = 'Cổng thanh toán SePay chưa được cấu hình trên server.';
        }
        throw new Error(message);
      }

      setSelectedPackage(null);
      const checkout = json.data?.checkout;
      if (!checkout?.checkoutUrl || !checkout?.fields) {
        throw new Error('Thiếu thông tin cổng thanh toán SePay');
      }
      if (json.data?.invoiceNumber) {
        sessionStorage.setItem('tamcare_pending_order', json.data.invoiceNumber);
      }
      submitSepayCheckout(checkout);
    } catch (err) {
      setError(err.message || 'Không thể tạo đơn thanh toán');
      setSubmitting(false);
    }
  };

  if (!authReady || !data?.id) {
    return (
      <div className="tamcare-membership-page">
        <div className="tamcare-membership-container" style={{ textAlign: 'center', paddingTop: 48 }}>
          <p>Đang kiểm tra phiên đăng nhập...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tamcare-membership-page">
      <div className="tamcare-membership-container">
        <div style={{ marginBottom: 16 }}>
          <Link to="/profile" style={{ textDecoration: 'none', color: '#2c5282', fontWeight: 600 }}>
            <i className="fa-solid fa-arrow-left" /> Quay lại Hồ sơ
          </Link>
        </div>

        {success && <div className="tamcare-membership-notice tamcare-membership-notice--success">{success}</div>}
        {error && <div className="tamcare-membership-notice tamcare-membership-notice--error">{error}</div>}

        {!loading && membership && (
          <div className="tamcare-membership-status">
            <div className="tamcare-membership-status__grid">
              <div>
                <div className="tamcare-membership-status__label">Trạng thái sử dụng</div>
                <div className="tamcare-membership-status__title">
                  {membership.isPremium
                    ? 'Gói Pro đang hoạt động'
                    : 'Gói Miễn phí (blog, hồ sơ, cửa hàng)'}
                </div>
                <div className="tamcare-membership-status__sub">
                  {!membership.isLinked
                    ? 'Liên kết gia đình trước, sau đó người thân mua gói cho từng người cao tuổi.'
                    : membership.premiumExpiry
                      ? `Hạn sử dụng đến ${formatDateTime(membership.premiumExpiry)}`
                      : 'Mỗi người cao tuổi cần một gói riêng.'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="tamcare-membership-status__label">Gói hiện tại</div>
                <div
                  className={`tamcare-membership-status__title ${membership.memberLevel > 0 ? 'tamcare-membership-status__active' : ''}`}
                  style={{ fontSize: 20 }}
                >
                  {memberLevelLabel(membership.memberLevel)}
                </div>
                <div className="tamcare-membership-status__sub">
                  {`Loại gói: ${normalizeTierLabel(membership.tierLabel)}`}
                  {membership.isLinked ? ' · Đã liên kết gia đình' : ' · Chưa liên kết gia đình'}
                </div>
              </div>
            </div>
          </div>
        )}

        {isRelative && linkedElderlies.length > 0 && (
          <div className="tamcare-membership-info-card" style={{ marginBottom: 20 }}>
            <h4><i className="fa-solid fa-users" /> Chọn người cao tuổi cần mua gói</h4>
            <p style={{ color: '#64748b', fontSize: 14, marginTop: 0 }}>
              Mỗi người cao tuổi = một lần thanh toán riêng. Gói chỉ áp dụng cho người được chọn.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              {linkedElderlies.map((elderly) => (
                <label
                  key={elderly.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    border: `2px solid ${String(selectedElderlyId) === String(elderly.id) ? '#2563eb' : '#e2e8f0'}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    background: String(selectedElderlyId) === String(elderly.id) ? '#eff6ff' : '#fff',
                  }}
                >
                  <input
                    type="radio"
                    name="elderly"
                    value={elderly.id}
                    checked={String(selectedElderlyId) === String(elderly.id)}
                    onChange={() => setSelectedElderlyId(String(elderly.id))}
                  />
                  <div style={{ flex: 1 }}>
                    <strong>{elderly.fullName || `Người cao tuổi #${elderly.id}`}</strong>
                    <div style={{ fontSize: 13, color: '#64748b' }}>
                      Gói: {normalizeTierLabel(elderly.tierLabel)}
                      {elderly.expiresAt ? ` · Hết hạn ${formatDateTime(elderly.expiresAt)}` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {isRelative && (
          <div
            className={`tamcare-membership-notice ${linkedElderlies.length === 0 ? 'tamcare-membership-notice--error' : ''}`}
            style={{ marginBottom: 16 }}
          >
            <strong>Bước mua gói:</strong> (1) Liên kết người cao tuổi bằng mã TC → (2) Chọn người cần mua gói → (3) Thanh toán SePay.
            {linkedElderlies.length === 0 ? (
              <>
                {' '}
                Hiện bạn <strong>chưa liên kết</strong> ai — vui lòng{' '}
                <Link to="/my-relatives" style={{ fontWeight: 700, color: 'inherit', textDecoration: 'underline' }}>
                  liên kết trước
                </Link>{' '}
                thì mới bấm được &quot;Thanh toán SePay&quot;.
              </>
            ) : (
              <> Mỗi người cao tuổi cần mua gói riêng.</>
            )}
          </div>
        )}

        {isRelative && linkedElderlies.length === 0 && (
          <div className="tamcare-membership-info-card" style={{ marginBottom: 20, borderColor: '#fecaca', background: '#fffafb' }}>
            <h4 style={{ color: '#b91c1c', marginBottom: 8 }}>
              <i className="fa-solid fa-link" /> Chưa thể thanh toán
            </h4>
            <p style={{ margin: 0, color: '#7f1d1d', lineHeight: 1.6 }}>
              Nút thanh toán đang tắt vì bạn chưa liên kết người cao tuổi nào. Hãy vào trang{' '}
              <Link to="/my-relatives" style={{ fontWeight: 700 }}>Gia đình</Link>, nhập mã liên kết (ví dụ TC123456)
              từ người cao tuổi, sau đó quay lại đây để chọn người và mua gói.
            </p>
          </div>
        )}

        {isElderly && membership?.elderlyMembership && (
          <div className="tamcare-membership-notice" style={{ marginBottom: 16 }}>
            Gói của bạn: <strong>{normalizeTierLabel(membership.elderlyMembership.tierLabel)}</strong>
            {membership.elderlyMembership.expiresAt
              ? ` · Hết hạn ${formatDateTime(membership.elderlyMembership.expiresAt)}`
              : ''}
            . Người thân sẽ mua gói thay bạn.
          </div>
        )}

        <div className="tamcare-membership-hero">
          <h1>
            Gói hội viên <span>TamCare</span>
          </h1>
          <p>
            Miễn phí: blog, lưu hồ sơ bệnh án, mua hàng. Toàn bộ tính năng còn lại: Pro 119.000đ/tháng (SePay).
          </p>
        </div>

        <div className="tamcare-membership-info-card" style={{ marginBottom: 20 }}>
          <h4><i className="fa-solid fa-gift" /> Gói Miễn phí</h4>
          <ul className="tamcare-membership-card__features" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
            {FREE_TIER_FEATURES.map((feature) => (
              <li key={feature} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <i className="fa-solid fa-check" style={{ color: '#16a34a' }} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="tamcare-membership-pricing">
          {packages.map((pkg) => (
            <article
              key={pkg.id}
              className={`tamcare-membership-card ${pkg.popular ? 'tamcare-membership-card--popular' : ''}`}
            >
              {pkg.popular && <div className="tamcare-membership-card__badge">PHỔ BIẾN</div>}
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#2563eb', marginBottom: 6 }}>
                {packageTierLabel()}
              </div>
              <h4 style={{ margin: 0 }}>{pkg.name}</h4>
              <div className="tamcare-membership-card__price">{formatVnd(pkg.cost)}</div>
              <ul className="tamcare-membership-card__features">
                {(pkg.features || []).map((feature) => (
                  <li key={feature}>
                    <i className="fa-solid fa-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="tamcare-membership-btn"
                disabled={loading || submitting || !isRelative || !selectedElderlyId}
                onClick={() => setSelectedPackage(pkg)}
                title={
                  !isRelative
                    ? 'Chỉ người thân mới có thể mua gói'
                    : linkedElderlies.length === 0
                      ? 'Vui lòng liên kết người cao tuổi trước khi thanh toán'
                      : !selectedElderlyId
                        ? 'Vui lòng chọn người cao tuổi cần mua gói'
                        : undefined
                }
              >
                {!isRelative
                  ? 'Người thân mua gói'
                  : linkedElderlies.length === 0
                    ? 'Liên kết trước khi mua'
                    : 'Thanh toán SePay'}
              </button>
            </article>
          ))}
        </div>

        <div className="tamcare-membership-info-card">
          <h4><i className="fa-solid fa-circle-info" /> Lưu ý</h4>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#64748b', lineHeight: 1.7 }}>
            <li>
              <strong>Người thân:</strong> phải{' '}
              <Link to="/my-relatives">liên kết người cao tuổi</Link> trước, rồi mới chọn người và thanh toán SePay.
            </li>
            <li>Gia hạn Pro → cộng dồn thời hạn.</li>
            <li>Hủy liên kết gia đình → gói của người cao tuổi đó bị hủy.</li>
          </ul>
        </div>
      </div>

      {selectedPackage && (
        <div
          className="tamcare-membership-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => !submitting && setSelectedPackage(null)}
        >
          <div className="tamcare-membership-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="tamcare-membership-modal__header">
              <h3>Xác nhận thanh toán SePay</h3>
              <button
                type="button"
                className="tamcare-membership-modal__close"
                aria-label="Đóng"
                onClick={() => !submitting && setSelectedPackage(null)}
              >
                &times;
              </button>
            </div>
            <div className="tamcare-membership-modal__body">
              <div className="tamcare-membership-modal__product">
                <div className="tamcare-membership-modal__icon">
                  <i className="fa-solid fa-gem" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>{selectedPackage.name}</div>
                  <div style={{ color: '#888', fontSize: 12 }}>
                    Cho: {selectedElderly?.fullName || 'Người cao tuổi'}
                  </div>
                </div>
                <div style={{ fontWeight: 700 }}>{formatVnd(modalAmount)}</div>
              </div>
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 16 }}>
                Bạn sẽ được chuyển sang trang thanh toán SePay (QR chuyển khoản). Sau khi thanh toán thành công, gói sẽ tự động kích hoạt.
              </p>
            </div>
            <div className="tamcare-membership-modal__footer">
              <button
                type="button"
                className="tamcare-membership-modal__submit"
                disabled={submitting}
                onClick={handlePurchase}
              >
                {submitting ? 'Đang chuyển SePay...' : 'Tiếp tục thanh toán'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipPage;
